import {
  Form,
  PrimaryButton,
  type SelectValue,
  SecondaryButton,
  Space,
  SubText,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { useGlobalCommandOrgCategoriesForm } from '../../hooks/useGlobalCommandOrgCategoriesForm'
import messages from '../../messages'
import {
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormSecondaryCategory,
} from '../../types'

export interface IGlobalCommandOrgCategoriesFormContentProps {
  primaryCategories: IGlobalCommandOrgCategoriesFormPrimaryCategory[]
  primaryCategoryLabel?: string
  secondaryCategoryLabel?: string
  helperText?: string
  disabled?: boolean
  onCancel: () => void
}

export const GlobalCommandOrgCategoriesFormContent = ({
  primaryCategories,
  primaryCategoryLabel,
  secondaryCategoryLabel,
  helperText,
  disabled,
  onCancel,
}: IGlobalCommandOrgCategoriesFormContentProps) => {
  const intl = useProcurifyIntl()

  const {
    primaryCategoryOptions,
    secondaryCategoryOptions,
    isSecondaryCategoryVisible,
    isSecondaryCategoryDisabled,
    handlePrimaryCategoryChange,
  } = useGlobalCommandOrgCategoriesForm({ primaryCategories })

  const primaryLabel =
    primaryCategoryLabel ?? intl.formatMessage(messages.primaryCategoryLabel)
  const primaryPlaceholder = primaryCategoryLabel
    ? intl.formatMessage(messages.agentLabelPlaceholder, {
        label: primaryCategoryLabel,
      })
    : intl.formatMessage(messages.primaryCategoryPlaceholder)

  const secondaryLabel =
    secondaryCategoryLabel ??
    intl.formatMessage(messages.secondaryCategoryLabel)
  const secondaryPlaceholder = secondaryCategoryLabel
    ? intl.formatMessage(messages.agentLabelPlaceholder, {
        label: secondaryCategoryLabel,
      })
    : intl.formatMessage(messages.secondaryCategoryPlaceholder)

  return (
    <Form.Content>
      <Space size='large' direction='vertical' align='stretch'>
        <Space size='medium' direction='vertical' align='stretch'>
          {helperText && <SubText color='textSecondary'>{helperText}</SubText>}
          <Form.Select<
            SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory>
          >
            id='GlobalCommandOrgCategoriesFormPrimaryCategory'
            name='primaryCategory'
            label={primaryLabel}
            placeholder={primaryPlaceholder}
            options={primaryCategoryOptions}
            isDisabled={disabled}
            menuPortalTarget={document.body}
            onChange={handlePrimaryCategoryChange}
            analyticsId='global-command-org-categories-form-primary-category'
            required
            fullWidth
          />

          {isSecondaryCategoryVisible && (
            <Form.Select<
              SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory>
            >
              id='GlobalCommandOrgCategoriesFormSecondaryCategory'
              name='secondaryCategory'
              label={secondaryLabel}
              placeholder={secondaryPlaceholder}
              options={secondaryCategoryOptions}
              isDisabled={disabled || isSecondaryCategoryDisabled}
              menuPortalTarget={document.body}
              analyticsId='global-command-org-categories-form-secondary-category'
              required={!isSecondaryCategoryDisabled}
              fullWidth
            />
          )}
        </Space>

        <Space size='medium' direction='horizontal' align='stretch'>
          <Space.Item grow={1} flex>
            <SecondaryButton
              type='button'
              fullWidth
              disabled={disabled}
              onClick={onCancel}
              analyticsId='global-command-org-categories-form-cancel'
            >
              {intl.formatMessage(messages.cancelButtonText)}
            </SecondaryButton>
          </Space.Item>

          <Space.Item grow={1} flex>
            <PrimaryButton
              type='submit'
              fullWidth
              disabled={disabled}
              analyticsId='global-command-org-categories-form-save'
            >
              {intl.formatMessage(messages.saveButtonText)}
            </PrimaryButton>
          </Space.Item>
        </Space>
      </Space>
    </Form.Content>
  )
}
